import { useSEO } from '../../hooks/useSEO';
import SocialMediaHero from './components/SocialMediaHero';
import SocialMediaServices from './components/SocialMediaServices';
import SocialMediaPortfolio from './components/SocialMediaPortfolio';
import SocialMediaProcess from './components/SocialMediaProcess';
import SocialMediaCTA from './components/SocialMediaCTA';

const SocialMediaPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bamme-media.de';

  useSEO({
    title: 'Social Media Marketing Wernigerode | Bamme-Media',
    description: 'Professionelles Social Media Marketing in Wernigerode von Bamme-Media. Instagram, Facebook, TikTok und LinkedIn Betreuung für mehr Reichweite, Engagement und Kunden. Content Creation inklusive!',
    keywords: 'Social Media Marketing Wernigerode, Instagram Marketing, Facebook Marketing, TikTok Marketing, LinkedIn Marketing, Content Creation, Social Media Betreuung, Bamme-Media',
    canonical: `${siteUrl}/social-media`,
    robots: 'index, follow',
    ogTitle: 'Social Media Marketing Wernigerode | Bamme-Media',
    ogDescription: 'Professionelles Social Media Marketing in Wernigerode von Bamme-Media. Instagram, Facebook, TikTok und LinkedIn Betreuung für mehr Reichweite, Engagement und Kunden.',
    ogImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    ogImageWidth: '1200',
    ogImageHeight: '630',
    ogImageAlt: 'Bamme-Media Social Media Marketing Services in Wernigerode',
    twitterTitle: 'Social Media Marketing Wernigerode | Bamme-Media',
    twitterDescription: 'Professionelles Social Media Marketing in Wernigerode von Bamme-Media. Instagram, Facebook, TikTok und LinkedIn Betreuung für mehr Reichweite, Engagement und Kunden.',
    twitterImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    twitterImageAlt: 'Bamme-Media Social Media Marketing Services in Wernigerode',
    geoPosition: '51.8312,10.7865',
    geoRegion: 'DE-ST',
    geoPlacename: 'Wernigerode',
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Social Media Marketing",
          "description": "Professionelles Social Media Marketing in Wernigerode von Bamme-Media. Instagram, Facebook, TikTok und LinkedIn Betreuung für mehr Reichweite, Engagement und Kunden.",
          "provider": {
            "@type": "Organization",
            "name": "Bamme-Media",
            "url": siteUrl,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Wernigerode",
              "postalCode": "38855",
              "addressRegion": "Sachsen-Anhalt",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "51.8312",
              "longitude": "10.7865"
            }
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Wernigerode"
            },
            {
              "@type": "Place",
              "name": "Harz"
            },
            {
              "@type": "Place",
              "name": "Sachsen-Anhalt"
            }
          ],
          "serviceType": "Social Media Marketing",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Social Media Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Instagram Marketing",
                  "description": "Professionelle Instagram Betreuung, Content Creation und Community Management"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Facebook Marketing",
                  "description": "Facebook Seiten Management, Werbeanzeigen und Content-Strategie"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "TikTok Marketing",
                  "description": "TikTok Content Creation und Viral Marketing Strategien"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Content Creation",
                  "description": "Erstellung von ansprechenden Social Media Inhalten und visuellen Konzepten"
                }
              }
            ]
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Welche Social Media Plattformen betreut ihr?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Wir betreuen alle relevanten Plattformen: Instagram, Facebook, TikTok, LinkedIn, YouTube und Pinterest. Je nach Zielgruppe und Geschäftsmodell empfehlen wir die optimalen Kanäle für Ihr Unternehmen und entwickeln eine maßgeschneiderte Multi-Channel-Strategie."
              }
            },
            {
              "@type": "Question",
              "name": "Wie oft wird Content auf meinen Kanälen gepostet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Die Posting-Frequenz hängt von der Plattform und Ihrem Paket ab. Typischerweise empfehlen wir: Instagram 4-7x pro Woche, Facebook 3-5x pro Woche, LinkedIn 2-3x pro Woche und TikTok täglich. Wir erstellen einen individuellen Content-Plan, der zu Ihrer Marke passt."
              }
            },
            {
              "@type": "Question",
              "name": "Erstellt ihr auch bezahlte Werbeanzeigen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ja, wir erstellen und verwalten professionelle Social Media Ads auf allen Plattformen. Von der Zielgruppenanalyse über Anzeigengestaltung bis zur Performance-Optimierung – wir sorgen für maximale Reichweite und ROI bei Ihrem Werbebudget."
              }
            },
            {
              "@type": "Question",
              "name": "Wie messe ich den Erfolg meiner Social Media Aktivitäten?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sie erhalten monatliche Reports mit allen wichtigen KPIs: Reichweite, Engagement-Rate, Follower-Wachstum, Website-Traffic, Lead-Generierung und Conversions. Wir nutzen professionelle Analytics-Tools und zeigen Ihnen transparent, wie sich Ihre Investition auszahlt."
              }
            },
            {
              "@type": "Question",
              "name": "Kann ich meinen Social Media Account selbst weiter nutzen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Selbstverständlich! Sie behalten die volle Kontrolle über Ihre Accounts. Wir arbeiten als Team-Mitglied mit Ihnen zusammen. Sie können jederzeit selbst posten, Stories teilen oder auf Kommentare antworten – wir koordinieren uns mit Ihnen."
              }
            },
            {
              "@type": "Question",
              "name": "Was ist in der monatlichen Social Media Betreuung enthalten?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Unsere Full-Service-Betreuung umfasst: Content-Erstellung (Grafiken, Videos, Texte), Content-Planung und -Veröffentlichung, Community Management (Kommentare, Nachrichten), Hashtag-Recherche, Performance-Tracking, monatliche Reports und strategische Beratung."
              }
            },
            {
              "@type": "Question",
              "name": "Wie lange dauert es, bis ich erste Ergebnisse sehe?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Erste Ergebnisse in Form von erhöhter Reichweite und Engagement sehen Sie bereits nach 2-4 Wochen. Für messbare Business-Ergebnisse wie Lead-Generierung sollten Sie 2-3 Monate einplanen, da Social Media Marketing ein langfristiger Prozess ist."
              }
            },
            {
              "@type": "Question",
              "name": "Kann ich die Inhalte vor der Veröffentlichung genehmigen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ja, wir erstellen einen Content-Kalender und senden Ihnen alle Inhalte zur Freigabe. Sie haben die volle Kontrolle und können Änderungswünsche äußern, bevor etwas veröffentlicht wird. Ihre Zufriedenheit steht an erster Stelle."
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen">
      <SocialMediaHero />
      <SocialMediaServices />
      <SocialMediaPortfolio />
      <SocialMediaProcess />
      <SocialMediaCTA />
    </div>
  );
};

export default SocialMediaPage;